import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

// internal hooks
import { useEscape } from "../../hooks/useEscape";
import { useVerifyUnique } from "../../hooks/useVerifyUnique";

// components
import InputField from "../../formComponents/InputField";

// styles and images
import Close from "../../assets/close.svg";
import "./AddCompany.css";

// store
import {
  userStore,
  triggersStore,
  messageAndErrorStore,
  dbDataStore,
} from "../../store/Store";
import FormButton from "../../formComponents/FormButton";
import useGetFetchFromId from "../../hooks/useGetFetchFromId";
import DropDown from "../../formComponents/DropDown";
import ErrorDisplay from "../../components/ErrorDisplay";

export default function AddCompany(props) {
  // global state
  const currentUser = userStore((data) => data.currentUser);
  const JwtToken = userStore((data) => data.JwtToken);
  const currentCompany = userStore((data) => data.currentCompany);
  const setCompanyTrigger = triggersStore((data) => data.setCompanyTrigger);
  const setAuthError = messageAndErrorStore((data) => data.setAuthError);
  const toggleNewMessage = messageAndErrorStore(data => data.toggleNewMessage)
  const globalCompanies = dbDataStore((data) => data.globalCompanies);
  const companyData = dbDataStore((data) => data.companyData);
  const setCompanyData = dbDataStore((data) => data.setCompanyData);

  // state
  let id = 0;
  if (props.id) {
    id = props.id;
  }
  const [oldName, setOldName] = useState("");
  const [globalAcOptions, setGlobalAcOptions] = useState([]);
  const [isPending, setIsPending] = useState(false);
  const [selection, setSelection] = useState(null);
  const [company, setCompany] = useState({
    id,
    company_name: "",
    owner_company_id: currentCompany.id,
    contact_name: "",
    first_name: "",
    last_name: "",
    address: "",
    email: "",
    phone: "",
    created_by_id: currentUser.id,
    updated_by_id: currentUser.id,
    company_id: 0,
  });
  const navigate = useNavigate();
  const { exists } = useVerifyUnique(
    props.coDocs,
    "company_name",
    company.company_name,
    oldName,
  );
  const {isPending: companyPending, error, status } = useGetFetchFromId(
    `${process.env.REACT_APP_BACKEND}/pc/account-company/${id}`,
    setCompany,
    id,
    setOldName,
    "company_name",
  );

  const handleClose = () => {
    setCompanyTrigger(false);
    props.setDocId("");
  };
  useEscape(setCompanyTrigger, [props.setDocId], [""], isPending);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCompany({
      ...company,
      [name]: value,
    });
  };

  const handleSelect = (o) => {
    setSelection(o);
    if (o.value !== 0) {
      setCompany({
        ...company,
        company_id: o.value,
      });
    }
  };

  useEffect(() => {
    const options = [{ value: 0, label: "Add New Company" }];
    if (globalCompanies) {
      globalCompanies.forEach((c) => {
        if (companyData) {
          const existingCompany = companyData.filter(
            (x) => x.company_id === c.id,
          );
          if (existingCompany.length === 0) {
            options.push({ value: c.id, label: c.company_name });
          }
        } else {
          options.push({ value: c.id, label: c.company_name });
        }
      });
    }
    setGlobalAcOptions(options);
  }, [globalCompanies, props.id, companyData]);

  const handlePhone = (e) => {
    let value = e.target.value.replace(/[^0-9]/g, "");
    if (value.length > 3 && value.length <= 6) {
      value = value.slice(0, 3) + "-" + value.slice(3);
    } else if (value.length > 6) {
      value =
        value.slice(0, 3) + "-" + value.slice(3, 6) + "-" + value.slice(6, 10);
    }
    e.target.value = value;
    setCompany({
      ...company,
      phone: e.target.value,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsPending(true);
    if (exists) {
      toggleNewMessage("Company Already Exists");
      setIsPending(false);
      return;
    }
    let method = "POST";
    let type = "Added";
    if (id > 0) {
      method = "PATCH";
      type = "Updated";
    }
    const controller = new AbortController();
    const headers = new Headers();
    headers.append("Content-Type", "application/json");
    headers.append("Authorization", "Bearer " + JwtToken);
    var reqBody = {
      account_company: company,
      is_new: selection && selection.value === 0 ? true : false,
      is_update: selection && selection.value > 0 ? true : false,
    };

    reqBody.account_company.contact_name = `${company.first_name} ${company.last_name}`;
    const requestOptions = {
      method: method,
      headers: headers,
      body: JSON.stringify(reqBody),
    };
    
    fetch(
      `${process.env.REACT_APP_BACKEND}/pc/account-company/${company.id}`,
      requestOptions,
    )
      .then((res) => {
        if (!res.ok) {
          if (res.status === 401) {
            setAuthError(false);
            setCompany(false);
            setIsPending(false);
            navigate("/login");
          } else if (res.status === 409) {
            toggleNewMessage("Email already exists");
            setIsPending(false);
            return Promise.resolve();
          } else {
            toggleNewMessage("Something went wrong");
            setIsPending(false);
          }
        } else {
          return res.json();
        }
      })
      .then((data) => {
        if (!data) {
          setIsPending(false);
          return;
        } else {
          setCompanyData(data);
          toggleNewMessage(null, `Company ${type}`);
          setCompanyTrigger(false);
          setIsPending(false);
          props.setDocId("");
        }
      })
      .catch((_) => {
        toggleNewMessage("Something went wrong");
        setIsPending(false);
        return;
      })
      .finally(() => {
        setIsPending(false);
      });
    return () => controller.abort();
  };

  if (error) {
    return <ErrorDisplay message={error} code={status} />;
  }
  return !companyPending && (
    <div className="popup">
      <div className="popup-inner-add-company">
        {!props.id ? <h3>Add Company</h3> : <h3>Update Company</h3>}
        {!isPending && <img src={Close} onClick={handleClose} alt="close" />}
        <form onSubmit={handleSubmit}>
          {(!props.id || company.company_id === 0) &&
            (!selection || selection.value !== 0) && (
              <DropDown
                title="Choose Existing Company"
                name="company-select"
                options={globalAcOptions}
                onChange={handleSelect}
                placeholder="Existing Companies"
                value={selection}
                required={true}
              />
            )}
          {(selection && selection.value === 0) ? (
            <>
              <InputField
                title={"Company Name"}
                className={"form-control"}
                type={"text"}
                name={"company_name"}
                value={company.company_name || ""}
                onChange={handleChange}
                placeholder={"Enter Company Name"}
                required={true}
              />
              <InputField
                title={"First Name"}
                className={"form-control"}
                type={"text"}
                name={"first_name"}
                value={company.first_name || ""}
                onChange={handleChange}
                placeholder={"Enter First Name"}
                required={true}
              />
              <InputField
                title={"Last Name"}
                className={"form-control"}
                type={"text"}
                name={"last_name"}
                value={company.last_name || ""}
                onChange={handleChange}
                placeholder={"Enter Last Name"}
                required={true}
              />
              <InputField
                title={"Address"}
                className={"form-control"}
                type={"text"}
                name={"address"}
                value={company.address || ""}
                onChange={handleChange}
                placeholder={"Enter Company Address"}
              />
              <InputField
                title={"Email"}
                className={"form-control"}
                type={"email"}
                name={"email"}
                value={company.email || ""}
                onChange={handleChange}
                placeholder={"Enter Company Email"}
                required={true}
              />
              <InputField
                title={"Phone Number"}
                className={"form-control"}
                type={"text"}
                name={"phone"}
                value={company.phone || ""}
                onChange={handlePhone}
                placeholder={"55512346189"}
              />
            </>
          ) : company.company_id > 0 && (
            <>
              <InputField
                title={"Address"}
                className={"form-control"}
                type={"text"}
                name={"address"}
                value={company.address || ""}
                onChange={handleChange}
                placeholder={"Enter Company Address"}
              />
              <InputField
                title={"Phone Number"}
                className={"form-control"}
                type={"text"}
                name={"phone"}
                value={company.phone || ""}
                onChange={handlePhone}
                placeholder={"55512346189"}
              />
            </>
          )}
          <FormButton pending={isPending} name={"Submit"} />
        </form>
        {error && <p className="error">{error}</p>}
      </div>
    </div>
  );
}
